import type { CorrespondenceRecord, EventClock, EventRecord, Provenance } from "../api";
import { label } from "./Money";

/**
 * One record of what happened on an event, in order.
 *
 * It was spread across three cards — clocks in a table, letters in a list,
 * drafts in a sidebar — so the sequence, which is the thing a contractual
 * argument turns on, had to be assembled in the reader's head.
 *
 * The distinction the incident tools draw, and the one that matters most
 * here: something a person did reads differently from something the
 * contract did on its own. A deemed acceptance has no author. Marking
 * those the same way would hide the only fact that makes them frightening.
 */
type Kind = "human" | "automatic" | "pending";

interface Moment {
  date: string;
  what: string;
  detail?: string;
  kind: Kind;
  /** Where this was recorded, for an outcome nobody chose. */
  prov?: Provenance;
  clause?: string;
}

export function EventTimeline({ event, clocks, correspondence, provenance }: {
  event: EventRecord;
  clocks: EventClock[];
  correspondence: CorrespondenceRecord[];
  provenance?: Record<string, Provenance>;
}) {
  const moments: Moment[] = [];
  const add = (date: string | null, what: string, kind: Kind, detail?: string,
               extra?: Partial<Moment>) => {
    if (date) moments.push({ date, what, kind, detail, ...extra });
  };

  add(event.awareness_date, "Contractor became aware", "human");
  add(event.instruction_date, "Project Manager instructed", "human");
  add(event.notified_date, `${event.reference} notified`, "human");

  for (const letter of correspondence) {
    add(letter.sent_date, letter.subject || label(letter.category), "human",
        letter.confirmed_by ? `link confirmed by ${letter.confirmed_by}`
                            : "link proposed, not yet confirmed");
  }

  for (const clock of clocks) {
    add(clock.reply_due_date, `Reply due under clause ${clock.clause}`, "pending");
    add(clock.replied_date, `Replied under clause ${clock.clause}`, "human");
    add(clock.reminder_notice_date,
        `Failure to reply notified — clause ${clock.clause}`, "human",
        "this started the further period");
    if (clock.deemed_date) {
      const happened = ["deemed", "time_barred"].includes(clock.state);
      add(clock.deemed_date,
          happened ? `${label(clock.state)} under clause ${clock.clause}`
                   : `Outcome falls due — clause ${clock.clause}`,
          happened ? "automatic" : "pending",
          happened ? undefined : clock.default_outcome,
          happened ? { prov: provenance?.[clock.id], clause: clock.clause } : undefined);
    }
  }

  moments.sort((a, b) => a.date.localeCompare(b.date));

  if (moments.length === 0) {
    return <p className="empty">Nothing is dated on this event yet.</p>;
  }

  return (
    <ol className="tline">
      {moments.map((m, i) => (
        <li key={i} className={`tline__row tline__row--${m.kind}`}>
          <span className="tline__date mn">{m.date}</span>
          <span className="tline__mark" aria-hidden="true" />
          <span className="tline__what">
            {m.what}
            {m.kind === "automatic" && (
              /* The provenance of an outcome nobody chose, in the shape a
                 record is checked in: when, under what, by whom — and the
                 journal row it can be verified against. Prose saying
                 "nobody decided this" is a claim; this is a reference. */
              <span className="prov">
                <span className="prov__date mn">{m.date}</span>
                <span className="prov__sep">·</span>
                <span>by operation of clause {m.clause}</span>
                <span className="prov__sep">·</span>
                <span className="prov__none">no human decision</span>
                {m.prov?.journal_ref && (
                  <>
                    <span className="prov__sep">·</span>
                    <span className="prov__ref mn"
                      title={`Recorded in the audit journal at ${m.prov.recorded_at ?? "an unrecorded time"}`}>
                      journal {m.prov.journal_ref}
                    </span>
                  </>
                )}
              </span>
            )}
            {m.detail && <span className="tline__detail">{m.detail}</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}
